import React, { useState, useRef, useEffect } from 'react';
import { ShoppingCart, Shield, Star, ChevronRight, Instagram, MapPin, Mail, Phone, Menu, X, ShieldCheck, Tag, Truck, Headphones, Video, Car } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { products, testimonials, faqs, features, stats } from '../mock';

const LOGO_URL = "/logo.jpg";

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            videoElement.play().catch((error) => {
              console.log("Auto-play prevented:", error);
            });
          } else {
            videoElement.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(videoElement);

    return () => {
      if (videoElement) {
        observer.unobserve(videoElement);
      }
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      'shield-check': ShieldCheck,
      'tag': Tag,
      'truck': Truck,
      'headphones': Headphones
    };
    const IconComponent = icons[iconName];
    return IconComponent ? <IconComponent className="w-8 h-8" /> : null;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3">
              <img src={LOGO_URL} alt="Dashcam Store Albania" className="h-14 w-14 object-contain" />
              <div>
                <h1 className="font-bold text-xl text-gray-900">Dashcam Store</h1>
                <p className="text-sm text-gray-600">Albania</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('hero')} className="text-gray-700 hover:text-red-600 transition-colors font-medium">Kreu</button>
              <button onClick={() => scrollToSection('products')} className="text-gray-700 hover:text-red-600 transition-colors font-medium">Produktet</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-red-600 transition-colors font-medium">Rreth Nesh</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-gray-700 hover:text-red-600 transition-colors font-medium">Dëshmi</button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-700 hover:text-red-600 transition-colors font-medium">FAQ</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-red-600 transition-colors font-medium">Kontakt</button>
            </nav>

            <div className="hidden md:block">
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white transition-all duration-300 hover:shadow-lg"
                onClick={() => window.open('https://instagram.com/dashcam.store.albania', '_blank')}
              >
                <Instagram className="w-4 h-4 mr-2" />
                Porosit Tani
              </Button>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col space-y-3">
                <button onClick={() => scrollToSection('hero')} className="text-left py-2 text-gray-700 hover:text-red-600 transition-colors font-medium">Kreu</button>
                <button onClick={() => scrollToSection('products')} className="text-left py-2 text-gray-700 hover:text-red-600 transition-colors font-medium">Produktet</button>
                <button onClick={() => scrollToSection('about')} className="text-left py-2 text-gray-700 hover:text-red-600 transition-colors font-medium">Rreth Nesh</button>
                <button onClick={() => scrollToSection('testimonials')} className="text-left py-2 text-gray-700 hover:text-red-600 transition-colors font-medium">Dëshmi</button>
                <button onClick={() => scrollToSection('faq')} className="text-left py-2 text-gray-700 hover:text-red-600 transition-colors font-medium">FAQ</button>
                <button onClick={() => scrollToSection('contact')} className="text-left py-2 text-gray-700 hover:text-red-600 transition-colors font-medium">Kontakt</button>
                <Button 
                  className="bg-red-600 hover:bg-red-700 text-white w-full"
                  onClick={() => window.open('https://instagram.com/dashcam.store.albania', '_blank')}
                >
                  <Instagram className="w-4 h-4 mr-2" />
                  Porosit Tani
                </Button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="hero" className="relative py-20 lg:py-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-red-100 text-red-700 hover:bg-red-100 px-4 py-2 text-sm font-medium">
              Oferta e Vitit të Ri - Deri në 40% Zbritje
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight flex items-center justify-center gap-4 flex-wrap">
              <Video className="w-12 h-12 sm:w-16 sm:h-16 text-red-600" />
              <span>Dashcam AL – <span className="text-red-600">Syri Yt</span> në Rrugë!</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed flex items-center justify-center gap-2 flex-wrap">
              <Car className="w-6 h-6 text-red-600 flex-shrink-0" />
              <span>Dashcams & aksesorë auto të besueshëm. Mbroni veten, regjistroni çdo moment. Dërgesa në të gjithë Shqipërinë, Kosovën dhe Maqedoninë.</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg" 
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
                onClick={() => scrollToSection('products')}
              >
                Shiko Produktet
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-gray-300 hover:border-red-600 hover:text-red-600 px-8 py-6 text-lg transition-all duration-300"
                onClick={() => window.open('https://instagram.com/dashcam.store.albania', '_blank')}
              >
                <Instagram className="w-5 h-5 mr-2" />
                Na Ndiqni
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16">
              {stats.map((stat) => (
                <div key={stat.id} className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-red-600 mb-2">{stat.value}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Pse të Zgjidhni Ne?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Përvojë e shkëlqyer blerëse dhe produkte cilësore për sigurinë tuaj në rrugë</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <Card key={feature.id} className="border-2 hover:border-red-600 transition-all duration-300 hover:shadow-lg">
                <CardHeader>
                  <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center mb-4 text-red-600">
                    {getIcon(feature.icon)}
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Produktet Tona</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Zgjidhni dashcam-in perfekt për nevojat tuaja</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.slice(0, 3).map((product) => (
              <Card key={product.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-105 group">
                <div className="relative">
                  {product.badge && (
                    <Badge className="absolute top-4 left-4 z-10 bg-red-600 text-white">
                      {product.badge}
                    </Badge>
                  )}
                  <div className="aspect-square bg-white flex items-center justify-center p-8">
                    <img src={product.image} alt={product.name} className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300" />
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl">{product.name}</CardTitle>
                  <CardDescription className="text-base">{product.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-4">
                    {product.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <Shield className="w-4 h-4 mr-2 text-red-600" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-baseline gap-3">
                    <span className="text-3xl font-bold text-red-600">{product.price.toLocaleString()} L</span>
                    <span className="text-lg text-gray-400 line-through">{product.oldPrice.toLocaleString()} L</span>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full bg-red-600 hover:bg-red-700 text-white transition-all duration-300"
                    onClick={() => window.open('https://instagram.com/dashcam.store.albania', '_blank')}
                  >
                    <Instagram className="w-4 h-4 mr-2" />
                    Porosit në Instagram
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg transition-all duration-300 hover:shadow-xl"
              onClick={() => window.open('https://instagram.com/dashcam.store.albania', '_blank')}
            >
              <Instagram className="w-5 h-5 mr-2" />
              Shiko të Gjitha Produktet
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Unboxing Video Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Shiko Produktin në Veprim</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Video unboxing të produktit tonë. Shiko cilësinë dhe detajet përpara se të porositësh!</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-0">
                <video
                  ref={videoRef}
                  controls
                  loop
                  muted
                  playsInline
                  className="w-full h-auto"
                  poster="/camera1.png"
                >
                  <source src="/video.mp4" type="video/mp4" />
                  Shfletuesi juaj nuk mbështet video HTML5.
                </video>
              </CardContent>
              <CardFooter className="flex flex-col items-start p-6">
                <CardTitle className="text-xl mb-2">Unboxing - Dashcam Premium</CardTitle>
                <CardDescription>Shiko cilësinë e paketimit dhe të produktit tonë më të shitur. Videoja starton automatikisht kur kalon tek ky seksion!</CardDescription>
              </CardFooter>
            </Card>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Rreth Nesh</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <img src={LOGO_URL} alt="Dashcam Store Albania Logo" className="w-full max-w-md mx-auto" />
              </div>
              <div className="space-y-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  <strong className="text-red-600">Dashcam Store Albania</strong> është dyqani juaj i besuar për dashcams dhe aksesorë auto me cilësi të lartë.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Ne ofrojmë produkte të përzgjedhura me kujdes nga brendet më të mira në treg, me çmime konkurruese dhe shërbim të shkëlqyer. Misioni ynë është t'ju ndihmojmë të ndjeheni më të sigurt në rrugë përmes teknologjisë më të fundit të dashcam-eve.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Me dërgesa në të gjithë Shqipërinë, Kosovën dhe Maqedoninë, ne sjellim cilësinë deri në derën tuaj. Çdo produkt vjen me garanci dhe mbështetje teknike falas.
                </p>
                <div className="flex gap-4 pt-4">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Shield className="w-5 h-5 text-red-600" />
                    <span className="font-medium">12 Muaj Garanci</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Truck className="w-5 h-5 text-red-600" />
                    <span className="font-medium">Dërgesa Falas</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Çfarë Thonë Klientët Tanë</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Besimi i klientëve tanë është prioriteti ynë kryesor</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                      <p className="text-sm text-gray-500 flex items-center mt-1">
                        <MapPin className="w-3 h-3 mr-1" />
                        {testimonial.location}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm leading-relaxed">{testimonial.text}</p>
                  <p className="text-xs text-gray-400 mt-3">{testimonial.date}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Pyetje të Shpeshta</h2>
              <p className="text-lg text-gray-600">Gjeni përgjigjet për pyetjet më të zakonshme</p>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq) => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`} className="border border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-medium text-gray-900 hover:text-red-600 transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Na Kontaktoni</h2>
            <p className="text-lg text-gray-600 mb-12">Jemi këtu për t'ju ndihmuar me çdo pyetje</p>
            
            <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12">
              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <Card className="border-2 hover:border-red-600 transition-colors duration-300">
                  <CardHeader>
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                      <Instagram className="w-6 h-6 text-red-600" />
                    </div>
                    <CardTitle>Instagram</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">Na ndiqni dhe na kontaktoni në Instagram për porosi dhe pyetje</p>
                    <Button 
                      variant="outline" 
                      className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-colors"
                      onClick={() => window.open('https://instagram.com/dashcam.store.albania', '_blank')}
                    >
                      @dashcam.store.albania
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-2 hover:border-red-600 transition-colors duration-300">
                  <CardHeader>
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                      <Truck className="w-6 h-6 text-red-600" />
                    </div>
                    <CardTitle>Dërgesa</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">
                      Dërgojmë në të gjithë Shqipërinë, Kosovën dhe Maqedoninë. Dërgesa e shpejtë dhe e sigurt.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-8 border-2 border-red-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Gati për të Porositur?</h3>
                <p className="text-gray-600 mb-6">Na dërgoni një mesazh në Instagram dhe do t'ju përgjigjemi menjëherë!</p>
                <Button 
                  size="lg"
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg transition-all duration-300 hover:shadow-xl"
                  onClick={() => window.open('https://instagram.com/dashcam.store.albania', '_blank')}
                >
                  <Instagram className="w-5 h-5 mr-2" />
                  Dërgo Mesazh
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <img src={LOGO_URL} alt="Dashcam Store Albania" className="h-12 w-12 object-contain" />
                <div>
                  <h3 className="font-bold text-lg">Dashcam Store Albania</h3>
                  <p className="text-sm text-gray-400">Syri yt në rrugë</p>
                </div>
              </div>
              <p className="text-gray-400 mb-4 max-w-md">
                Dashcams & aksesorë auto të besueshëm. Mbroni veten, regjistroni çdo moment.
              </p>
              <Button 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-gray-900"
                onClick={() => window.open('https://instagram.com/dashcam.store.albania', '_blank')}
              >
                <Instagram className="w-4 h-4 mr-2" />
                Na Ndiqni
              </Button>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Linqe të Shpejta</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => scrollToSection('products')} className="hover:text-white transition-colors">Produktet</button></li>
                <li><button onClick={() => scrollToSection('about')} className="hover:text-white transition-colors">Rreth Nesh</button></li>
                <li><button onClick={() => scrollToSection('testimonials')} className="hover:text-white transition-colors">Dëshmi</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="hover:text-white transition-colors">FAQ</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-white transition-colors">Kontakt</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Informacione</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-start">
                  <Truck className="w-5 h-5 mr-2 mt-0.5 text-red-500" />
                  <span>Dërgesa në AL, XK, MK</span>
                </li>
                <li className="flex items-start">
                  <Shield className="w-5 h-5 mr-2 mt-0.5 text-red-500" />
                  <span>12 Muaj Garanci</span>
                </li>
                <li className="flex items-start">
                  <Instagram className="w-5 h-5 mr-2 mt-0.5 text-red-500" />
                  <span>@dashcam.store.albania</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} Dashcam Store Albania. Të gjitha të drejtat e rezervuara.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
